'use client'
import { useTranslate } from '@repo/shared/hooks'
import { type FormSchema, useAppForm } from '@repo/ui/hooks'

import {
  Button,
  Col,
  Form,
  FormField,
  FormItemWrapper,
  Icon,
  Row,
  Skeleton,
  Typography,
} from '@repo/ui/components'
import {
  OrderBoxAmountInput,
  OrderBoxPriceInput,
  OrderBoxSummary,
  OrderBoxBestSuggestion,
} from '.'
import type {
  OrderBoxFormValues,
  OrderBoxMode,
  OrderBoxPriceType,
  OrderBoxTab,
  FlatOrderData,
  FlatAsset,
} from './order-box.types'
import { BalanceSlider } from '../balance-slider'

import { useEffect, useImperativeHandle, forwardRef, useState } from 'react'
import { useRouter } from '@repo/shared/i18n/navigation'
import { useOrderBoxSchema } from '@repo/shared/schemas'
import dynamic from 'next/dynamic'

const OrderBoxSubmitButton = dynamic(
  async () => (await import('.')).OrderBoxSubmitButton,
  {
    ssr: false,
    loading: () => (
      <Skeleton className='w-full h-[46px] mt-4xl sm:relative fixed sm:bottom-md sm:left-0 left-md sm:right-0 right-md sm:w-auto' />
    ),
  },
)

interface OrderBoxFormProps {
  tab: OrderBoxTab
  priceType: OrderBoxPriceType
  availableAmount: number
  availableBalance: number
  onSubmit?: (data: OrderBoxFormValues) => void
  mode: OrderBoxMode
  inputCurrency: 'IRR' | 'TMN'
  outputCurrency: 'IRR' | 'TMN'
  instantPrice: number
  isLoading: boolean
  maxIRRAmount: number
  maxTokenAmount: number
  token: string
  selectedAsset?: FlatAsset
  onOtcPlaceOrder?: (params: any, callbacks?: any) => void
  flatOrderData?: FlatOrderData
  tradeStatus?: string
  setTab?: (tab: OrderBoxTab) => void
  minOrderSize?: number
  isBalanceLoading?: boolean
  isInstantPriceLoading?: boolean
  targetScale?: number
  baseAvailableBalance?: number
  affiliateCode?: string | null
  userId?: string
}

export interface OrderBoxFormRef {
  resetForm: () => void
}

interface OrderBoxEmptyStateProps {
  title: string
  buttonLabel: string
  onButtonClick: () => void
  showPlusIcon?: boolean
  isLoading?: boolean
}

const OrderBoxEmptyState = ({
  title,
  buttonLabel,
  onButtonClick,
  showPlusIcon = false,
  isLoading = false,
}: OrderBoxEmptyStateProps) => {
  if (isLoading) {
    return (
      <Col className='gap-xs my-[31px]'>
        <Skeleton className='h-10 w-full rounded-md' />
      </Col>
    )
  }

  return (
    <Row className='justify-between bg-negative-surface-5 p-xs rounded-md my-[31px]'>
      <Typography size='xs' color='text-negative-surface-100' weight='regular'>
        {title}
      </Typography>
      <Button
        type='button'
        size='xs'
        className='px-md'
        onClick={onButtonClick}
        startIcon={
          showPlusIcon ? (
            <Icon name='PlusLine' size='xs' color='fill-base-main' />
          ) : undefined
        }
      >
        {buttonLabel}
      </Button>
    </Row>
  )
}

export const OrderBoxForm = forwardRef<OrderBoxFormRef, OrderBoxFormProps>(
  (props, ref) => {
    const {
      tab,
      priceType,
      availableAmount,
      availableBalance,
      onSubmit,
      mode,
      inputCurrency,
      outputCurrency,
      instantPrice,
      isLoading,
      maxIRRAmount,
      maxTokenAmount,
      token,
      selectedAsset,
      onOtcPlaceOrder,
      flatOrderData,
      tradeStatus,
      setTab,
      minOrderSize,
      isBalanceLoading = false,
      isInstantPriceLoading = false,
      targetScale = 1,
      baseAvailableBalance = 0,
      affiliateCode,
      userId,
    } = props

    const { t } = useTranslate()
    const { push } = useRouter()
    const { schema } = useOrderBoxSchema({
      tab: tab as 'buy' | 'sell',
      priceType,
      availableBalance,
      instantPrice,
      inputCurrency,
      outputCurrency,
      maxTokenAmount,
      minOrderSize,
      targetScale,
    })
    const { form } = useAppForm({
      formSchema: schema,
      defaultValues: {
        amount: undefined,
        price: 0,
        range: 0,
        rangeIRRAmount: 0,
        total: 0,
      },
    })
    // Reset form when tab changes
    useEffect(() => {
      form.reset({
        amount: undefined,
        price: 0,
        range: 0,
        rangeIRRAmount: 0,
        total: 0,
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab])

    const fee = flatOrderData?.otcFee ?? 0
    const _total = baseAvailableBalance
    const feeCurrency = flatOrderData?.otcFeeCurrency ?? 'IRR'
    const total = instantPrice * (flatOrderData?.targetAmount ?? 0)

    const totalWithFee = total

    // Check if we should show zero total (when form is reset)
    const [isFormReset, setIsFormReset] = useState(false)

    // Create reset function that can be called from parent
    const resetForm = () => {
      form.reset({
        amount: undefined,
        price: 0,
        range: 0,
        rangeIRRAmount: 0,
        total: 0,
      })
      setIsFormReset(true)
      // Reset the flag after a short delay
      setTimeout(() => setIsFormReset(false), 100)
    }

    // Expose reset function to parent component
    useImperativeHandle(ref, () => ({
      resetForm,
    }))

    const handleSubmit = async (data: FormSchema<typeof schema>) => {
      onSubmit?.({
        ...data,
        amount:
          tab === 'sell'
            ? Number(data.amount) * targetScale
            : Number(data.amount),
      } as OrderBoxFormValues)
    }

    const handleAmountChange = (value: number) => {
      if (onOtcPlaceOrder) {
        onOtcPlaceOrder(
          {
            submit: false,
            targetCurrency: token,
            baseCurrency: inputCurrency,
            amount:
              tab === 'sell' ? Number((value * targetScale).toFixed(0)) : value,
            amountCurrency: token,
            affiliateCode,
            orderType: tab === 'buy' ? 'buy' : 'sell',
          },
          {
            onSuccess: (response: any) => {
              if (tab === 'buy') {
                const _value =
                  Math.ceil(
                    (response.data.order?.baseAmount / baseAvailableBalance) *
                      100,
                  ).toFixed(0) ?? 0
                form.setValue(
                  'rangeIRRAmount',
                  Number(_value) > 100 ? 100 : Number(_value),
                )
              } else {
                form.setValue(
                  'range',
                  Math.floor((value / maxTokenAmount!) * 100) ?? 0,
                )
              }
            },
          },
        )
      }
    }
    const tokenAmount = form.watch('amount')
    const tokenPrice = form.watch('price')

    // Update total when amount or price changes
    useEffect(() => {
      const price = priceType === 'market' ? instantPrice : tokenPrice || 0
      if (tokenAmount && price > 0) {
        const calculatedTotal = tokenAmount * price + fee
        form.setValue('total', calculatedTotal)
      } else if (!tokenAmount) {
        form.setValue('total', 0)
      }
    }, [tokenAmount, tokenPrice, instantPrice, priceType, fee, form])

    const handleRangeAmountChange = (value: number) => {
      if (onOtcPlaceOrder) {
        onOtcPlaceOrder(
          {
            submit: false,
            targetCurrency: token,
            baseCurrency: inputCurrency,
            amount:
              tab === 'sell' ? Number((value * targetScale).toFixed(0)) : value,
            amountCurrency: token,
            affiliateCode,
            orderType: tab === 'buy' ? 'buy' : 'sell',
          },
          {
            onSuccess: (response: any) => {
              if (tab === 'buy') {
                form.setValue(
                  'rangeIRRAmount',
                  response.data.order?.baseAmount ?? 0,
                )
              } else {
                form.setValue('amount', String(value) ?? undefined)
              }
            },
          },
        )
      }
    }

    const handleRangeIRRAmountChange = (value: number) => {
      if (onOtcPlaceOrder) {
        // const _value = form.watch('amount')
        onOtcPlaceOrder(
          {
            submit: false,
            targetCurrency: token,
            baseCurrency: inputCurrency,
            // amount: Number(_value),
            amount:
              tab === 'sell' ? Number((value * targetScale).toFixed(0)) : value,
            // amountCurrency: token,
            amountCurrency: inputCurrency,
            affiliateCode,
            orderType: 'buy',
          },
          {
            onSuccess: (response: any) => {
              form.setValue(
                'amount',
                String(response.data.order?.targetAmount / targetScale || 0),
              )
              // form.setValue('rangeIRRAmount', value)
            },
          },
        )
      }
    }

    const handleTotalChange = (value: number) => {
      // If total is 0, set amount to undefined as well
      if (value === 0) {
        form.setValue('amount', undefined)
        // Also update slider values
        if (tab === 'buy') {
          form.setValue('rangeIRRAmount', 0)
        } else {
          form.setValue('range', 0)
        }
        return
      }

      // Calculate the amount based on total (excluding fee)
      const price = priceType === 'market' ? instantPrice : tokenPrice || 0
      if (price > 0) {
        const amountWithoutFee = value - fee
        const calculatedAmount = amountWithoutFee / price
        form.setValue('amount', calculatedAmount)

        // Update slider values based on the calculated amount
        if (tab === 'buy') {
          // For buy tab, update rangeIRRAmount based on the total value
          form.setValue('rangeIRRAmount', value)
        } else {
          // For sell tab, update range based on the calculated amount
          form.setValue('range', calculatedAmount)
        }

        // Trigger the amount change handler to update the API
        handleAmountChange(calculatedAmount)
      }
    }

    const assetLabel = t(
      selectedAsset?.kind === 'crypto' || selectedAsset?.id === 'MALEK'
        ? 'common_token'
        : 'CM2',
    )

    return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className='flex flex-col justify-between h-full'
        >
          <Col className='gap-md'>
            {mode === 'order' && priceType === 'limit' && (
              <FormField
                control={form.control}
                name='price'
                render={({ field }) => (
                  <FormItemWrapper
                    title={t(
                      tab === 'buy' ? 'order_price' : 'order_price_sell',
                    )}
                    inputSize='md'
                  >
                    <Col>
                      <OrderBoxPriceInput {...field} tab={tab} />
                      {mode === 'order' && priceType === 'limit' && (
                        <OrderBoxBestSuggestion
                          type={tab === 'buy' ? 'sell' : 'buy'}
                          price={71300}
                        />
                      )}
                    </Col>
                  </FormItemWrapper>
                )}
              />
            )}
            <FormField
              control={form.control}
              name='amount'
              render={({ field }) => (
                <FormItemWrapper
                  title={t(
                    tab === 'buy' ? 'order_amount' : 'order_amount_sell',
                  )}
                  inputSize='md'
                >
                  <Col className='gap-4xl'>
                    <OrderBoxAmountInput
                      {...field}
                      onChange={(value) => {
                        field.onChange(value)
                        form.setValue('amount', value)
                      }}
                      onAmountChange={(value) => {
                        handleAmountChange(value)
                      }}
                      max={maxTokenAmount}
                      targetScale={targetScale}
                      tab={tab}
                      assetLabel={assetLabel}
                      scale={selectedAsset?.scale}
                    />
                  </Col>
                </FormItemWrapper>
              )}
            />
            <FormField
              control={form.control}
              name={tab === 'buy' ? 'rangeIRRAmount' : 'range'}
              render={({ field }) => {
                if (!userId && !selectedAsset && tab === 'buy') {
                  return (
                    <div className='h-[102px] flex justify-center items-center'>
                      <Skeleton className='h-10 w-full' />
                    </div>
                  )
                }
                if (!userId) {
                  return <></>
                }

                // For Buy Tab
                if (tab === 'buy') {
                  const isInsufficientBalance =
                    (availableBalance || 0) <
                      (instantPrice || 0) / targetScale ||
                    availableBalance === 0 ||
                    instantPrice === 0

                  if (isInsufficientBalance) {
                    return (
                      <OrderBoxEmptyState
                        title={t('common_insufficient_balance', {
                          type: t(`order_${tab}`),
                        })}
                        buttonLabel={t('common_deposit')}
                        onButtonClick={() =>
                          push('/wallet/deposit/online-payment')
                        }
                        showPlusIcon={true}
                        isLoading={isBalanceLoading || isInstantPriceLoading}
                      />
                    )
                  }

                  return (
                    <BalanceSlider
                      title={t('order_available_balance_buy')}
                      value={form.watch('rangeIRRAmount')}
                      onChange={(value) => {
                        field.onChange(value)
                        form.setValue('rangeIRRAmount', value)
                      }}
                      onCommit={(value) => {
                        handleRangeIRRAmountChange(value)
                      }}
                      max={maxIRRAmount}
                      currencyLabel={t('currency_toman')}
                      onMaxClick={() => push('/wallet/deposit/online-payment')}
                      color='positive'
                      availableBalance={(_total / 10).toString()}
                      disabled={false}
                      loading={isBalanceLoading || isInstantPriceLoading}
                    />
                  )
                }
                // For Sell Tab
                if (!token) {
                  // No token selected, show disabled BalanceSlider
                  return (
                    <BalanceSlider
                      title={t('order_available_balance_sell')}
                      {...field}
                      max={0}
                      onChange={(value) => {
                        field.onChange(value)
                        form.setValue('range', value)
                      }}
                      onCommit={(value) => {
                        handleRangeAmountChange(value)
                      }}
                      currencyLabel={assetLabel}
                      color='negative'
                      inputCurrency={inputCurrency}
                      outputCurrency={outputCurrency}
                      availableBalance='0'
                      disabled={true}
                      loading={isBalanceLoading || isInstantPriceLoading}
                    />
                  )
                }

                if (availableAmount === 0 && tab === 'sell') {
                  // Token selected but no sellable area
                  return (
                    <OrderBoxEmptyState
                      title={t('inapp_shared_no_sellable_area')}
                      buttonLabel={t('order_buy')}
                      onButtonClick={() => setTab?.('buy')}
                      showPlusIcon={false}
                      isLoading={isInstantPriceLoading}
                    />
                  )
                }

                // Check if balance is insufficient for selling
                const isInsufficientBalance = instantPrice === 0

                if (isInsufficientBalance && tab !== 'sell') {
                  return (
                    <OrderBoxEmptyState
                      title={t('common_insufficient_balance', {
                        type: t(`order_${tab}`),
                      })}
                      buttonLabel={t('order_buy')}
                      onButtonClick={() => setTab?.('buy')}
                      showPlusIcon={false}
                      isLoading={isBalanceLoading || isInstantPriceLoading}
                    />
                  )
                }
                // Show BalanceSlider when everything is OK
                return (
                  <BalanceSlider
                    title={t('order_available_balance_sell')}
                    {...field}
                    max={maxTokenAmount}
                    onChange={(value) => {
                      field.onChange(value)
                      form.setValue('range', value)
                    }}
                    onCommit={(value) => {
                      handleRangeAmountChange(value)
                    }}
                    currencyLabel={assetLabel}
                    color='negative'
                    inputCurrency={inputCurrency}
                    outputCurrency={outputCurrency}
                    availableBalance={(
                      availableAmount / selectedAsset?.scale!
                    ).toString()}
                    disabled={false}
                    tab='sell'
                    token={token}
                    loading={isBalanceLoading || isInstantPriceLoading}
                    kind={selectedAsset?.kind}
                  />
                )
              }}
            />
            <OrderBoxSummary
              total={isFormReset ? 0 : totalWithFee}
              feeCurrency={feeCurrency}
              fee={isFormReset ? 0 : fee}
              mode={mode}
              tab={tab as 'buy' | 'sell'}
              onTotalChange={handleTotalChange}
              isLoadingFee={isLoading}
              token={token}
              targetScale={targetScale}
              kind={selectedAsset?.kind}
            />
          </Col>
          <OrderBoxSubmitButton
            tab={tab}
            mode={mode}
            onClick={form.handleSubmit(handleSubmit)}
            isLoading={isLoading}
            tradeStatus={tradeStatus}
          />
        </form>
      </Form>
    )
  },
)
